import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Search, ArrowLeft, Clock, Eye } from 'lucide-react';
import { motion } from 'framer-motion';
import LoadingSpinner, { SkeletonCard } from '../components/LoadingSpinner';
import { useCategoryArticles } from '../hooks/useArticles';
import { formatDateShort, formatReadingTime } from '../utils/formatters';

const backendUrl = 'https://justurbane.in/backend_php/public';

const CategoryPage = () => {
  const { slug } = useParams();
  const [searchQuery, setSearchQuery] = useState('');

  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
  });


  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/categories`);
        const data = await res.json();
        setCategories(data);
      } catch (err) {
        console.error('Failed to load categories', err);
      }
    };

    fetchCategories();
  }, []);
  useEffect(() => {
  if (!categories.length) return;

  const cat = categories.find(
    c => c.name?.toLowerCase() === slug.toLowerCase()
  );

    setCurrentCategory(cat || null);
  }, [categories, slug]);


  const { data: articles = [], isLoading, error } = useCategoryArticles(slug, { limit: 50 });

  // Filter articles
  const filteredArticles = useMemo(() => {
    if (!articles.length) return [];

    let filtered = articles;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(query) ||
        article.dek?.toLowerCase().includes(query) ||
        article.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Sort by newest first
    filtered.sort((a, b) => new Date(b.published_at) - new Date(a.published_at));

    return filtered;
  }, [articles, searchQuery]);

  
  const pageTitle = currentCategory?.display_name || slug.toUpperCase();
  const categoryLabel = currentCategory?.description || "Category";



  // Subcategories for each main category
  const currentSubcategories = currentCategory?.subcategories || [];

  // Clean GQ India style Article Card
 const GQStyleCard = ({ article, index }) => {
  const categoryLabel = article.category?.name || "STYLE";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <Link
        to={`/article/${article.slug || article.id}`}
        className="group block bg-white border border-gray-100 transition-all duration-300 hover:shadow-xl"
      >
        {/* IMAGE CONTAINER: We use 'aspect-video' or a fixed height to ensure a frame exists */}
        <div className="relative w-full h-64 overflow-hidden bg-white-200">
          <img
            src={article.hero_image}
            alt={article.title}
            /* CRITICAL FIXES: 
               1. object-cover: Crops the image instead of stretching it.
               2. h-full w-full: Forces the image to fill the container.
            */
            className="absolute inset-0 w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
            onError={(e) => {
              e.target.src = '/placeholder-image.jpg';
            }}
          />
        </div>

        {/* CONTENT SECTION */}
        <div className="p-5 flex flex-col items-start text-left">
          {/* Category Label: Tight, bold, and tracked out */}
          <span className="inline-block text-[10px] font-black tracking-[0.15em] text-gray-400 uppercase mb-3 border-b-2 border-black pb-1">
            {categoryLabel}
          </span>
          
          {/* Title: Serif font is key for an article aesthetic */}
          <h3 className="text-2xl font-serif font-semibold text-gray-900 leading-tight mb-4 group-hover:text-gray-700">
            {article.title}
          </h3>
          
          {/* Author & Date: Clean, minimal line */}
          <div className="w-full flex justify-between items-center pt-4 border-t border-gray-100 mt-2">
            <span className="text-xs font-bold text-gray-900 uppercase italic">
              By {article.author_name}
            </span>
            <span className="text-xs text-gray-400 font-medium">
              
                  {article.published_at
                    ? formatDate(article.published_at)
                    : formatDate(article.created_at)}
            </span> 
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Category Not Found</h1>
          <Link to="/" className="btn-primary">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">

      <nav className="container mx-auto px-4 text-gray-600 text-sm">
        <Link to="/" className="hover:text-gray-900">Home</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-900 capitalize">{slug}</span>
      </nav>
        
        {/* Page Title - Exactly like GQ India */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-6xl font-black text-gray-900 mb-8 tracking-wider">
            {pageTitle}
          </h1>
        </motion.div>

        {/* Subcategories Navigation - If Available */}
        {currentSubcategories.length > 0 && (
          <motion.div 
            className="mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="flex flex-wrap justify-center gap-4">
              {currentSubcategories.map((sub) => (
                <Link
                  key={sub}
                  to={`/category/${slug}/${sub}`}
                  className="group bg-gray-100 hover:bg-primary-600 text-gray-700 hover:text-white px-6 py-3 rounded-full text-sm font-bold uppercase tracking-wide transition-all duration-300"
                >
                  {sub.replace('-', ' ')}
                </Link>
              ))}

            </div>
          </motion.div>
        )}

        {/* Search Bar - Clean and Simple */}
        <motion.div 
          className="max-w-lg mx-auto mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={`Search ${pageTitle.toLowerCase()} articles...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-200 focus:border-gray-300 outline-none text-lg"
            />
          </div>
        </motion.div>

        {/* Articles Grid - Clean GQ India Style */}
{isLoading ? (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
    {[...Array(6)].map((_, i) => (
      <SkeletonCard key={i} />
    ))}
  </div>
) : filteredArticles.length > 0 ? (
  <motion.div 
    className="grid grid-cols-1 md:grid-cols-3 gap-8"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.6, delay: 0.6 }}
  >
    {filteredArticles.map((article, index) => (
      <GQStyleCard 
        key={article.id} 
        article={article} 
        index={index}
      />
    ))}
  </motion.div>
) : (
  <motion.div 
    className="text-center py-20"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.6, delay: 0.6 }}
  >
    <h3 className="text-2xl font-bold text-gray-900 mb-4">
      {searchQuery ? 'No Articles Found' : 'Coming Soon'}
    </h3>
    <p className="text-gray-600 mb-8 max-w-md mx-auto">
      {searchQuery 
        ? `No articles match "${searchQuery}"`
        : `We're working on amazing ${pageTitle.toLowerCase()} content.`
      }
    </p>
    {searchQuery && (
      <button
        onClick={() => setSearchQuery('')}
        className="px-6 py-3 bg-gray-200 text-gray-900 rounded-lg font-medium mr-4 hover:bg-gray-300 transition-colors"
      >
        Clear Search
      </button>
    )}
    <Link 
      to="/" 
      className="inline-block px-6 py-3 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
    >
      Back to Home
    </Link>
  </motion.div>
)}

        {/* Back Navigation */}
        {filteredArticles.length > 0 && (
          <motion.div 
            className="text-center mt-16 pt-8 border-t border-gray-100"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <Link
              to="/"
              className="inline-flex items-center text-gray-600 hover:text-gray-900 font-medium"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;